import { KeyboardEvent } from 'react'

import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'

import styles from '~/bootcamp/Activities/components/decimalPlacement.module.css'

export default function DecimalCommaSlot({
  ariaLabel,
  colorInfo,
  decimalSign,
  enterDelayMs,
  index,
  isPlaced,
  isRowError,
  isWrong,
  onClick,
  onKeyDown,
  registerSlot,
}: {
  ariaLabel: string
  colorInfo: CategoryColorClasses
  decimalSign: string
  enterDelayMs: number
  index: number
  isPlaced: boolean
  isRowError?: boolean
  isWrong?: boolean
  onClick: (index: number) => void
  onKeyDown: (event: KeyboardEvent, index: number) => void
  registerSlot: (index: number, element: HTMLElement | null) => void
}): React.ReactElement {
  // An empty place is a dotted ring holding a pale sign; the chosen one fills solid with the sign
  // knocked out in white. Filling it is what makes the choice unmistakable, and the pale sign is
  // what keeps the empty places from competing with it — the ring itself stays legible so it can
  // still be found and pressed on a phone. Both states keep the same box, so the sign never
  // appears to shrink or shift when it is placed.
  const filledClass = isWrong ? 'bg-red border-red' : `${colorInfo.bgClass} ${colorInfo.borderClass}`
  const emptyClass = isRowError ? 'border-red text-red' : `${colorInfo.borderClass} ${colorInfo.textClass}`
  const ringClass = isPlaced
    ? `text-white ${filledClass}`
    : `border-dotted bg-white opacity-60 hover:opacity-100 ${emptyClass}`

  return (
    // Straddles the column divider without taking part in the row's layout, so the answer row
    // keeps the exact width and column pitch of the rows above it. It hangs below the digits so
    // they stay easy to reach and correct, and the chosen sign lifts to mark itself out.
    <button
      aria-label={ariaLabel}
      aria-pressed={isPlaced}
      className={`absolute -bottom-4 right-0 z-1 flex h-8 w-6 translate-x-1/2 items-center justify-center rounded-full focus-visible:outline-0 focus-visible:ring-2 focus-visible:ring-current ${styles['socket']}`}
      onClick={() => onClick(index)}
      onKeyDown={(event) => onKeyDown(event, index)}
      ref={(element) => registerSlot(index, element)}
      style={{ animationDelay: `${enterDelayMs}ms` }}
      type="button"
    >
      <span
        className={`flex h-7 w-5 items-center justify-center rounded-full border-2 transition-all sm:h-9 sm:w-6 ${
          isPlaced ? '-translate-y-1' : ''
        } ${ringClass}`}
      >
        {/* The sign sits on the baseline, so it needs nudging up to look centred in the ring. */}
        <span className={`font-bold text-md sm:text-lg leading-none -translate-y-2 ${isPlaced ? '' : 'opacity-50'}`}>
          {decimalSign}
        </span>
      </span>
    </button>
  )
}
